/**
 * Module CookieConsent v3 (orestbida)
 * Configuration conforme RGPD / CNIL & Google Consent Mode v2 pour le thème Sountsou 2026.
 *
 * @package Sountsou2026
 */

import * as CookieConsent from 'vanilla-cookieconsent';

/**
 * Met à jour les signaux de consentement pour Google Consent Mode v2 et le dataLayer
 *
 * @param {Object} cookie Objet retourné par CookieConsent
 */
function updateConsentSignals(cookie) {
    const isAnalyticsAccepted = CookieConsent.acceptedCategory('analytics');
    const isMarketingAccepted = CookieConsent.acceptedCategory('marketing');

    const analyticsSignal = isAnalyticsAccepted ? 'granted' : 'denied';
    const marketingSignal = isMarketingAccepted ? 'granted' : 'denied';

    // 1. Google Consent Mode v2 (si gtag existe)
    if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
            'analytics_storage': analyticsSignal,
            'ad_storage': marketingSignal,
            'ad_user_data': marketingSignal,
            'ad_personalization': marketingSignal
        });
    }

    // 2. dataLayer push pour déclencheurs GTM
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'cookie_consent_update',
        consent_analytics: analyticsSignal,
        consent_marketing: marketingSignal,
        consent_categories: (cookie && cookie.categories) ? cookie.categories : []
    });

    // 3. CustomEvent standard dans le DOM pour les scripts tiers
    window.dispatchEvent(new CustomEvent('cookieconsent:update', {
        detail: {
            analytics: isAnalyticsAccepted,
            marketing: isMarketingAccepted,
            cookie: cookie
        }
    }));
}

/**
 * Initialise le plugin CookieConsent v3
 */
export function initCookieConsent() {
    // Ne jamais exécuter CookieConsent dans l'administration WordPress (Gutenberg ou autre)
    if (typeof document !== 'undefined' && document.body && document.body.classList.contains('wp-admin')) {
        return;
    }

    CookieConsent.run({
        root: 'body',
        autoShow: true,
        disablePageInteraction: false,
        manageScriptTags: true,
        autoClearCookies: true,

        cookie: {
            name: 'cc_cookie',
            expiresAfterDays: 182, // 6 mois (recommandation CNIL)
            sameSite: 'Lax',
            secure: window.location.protocol === 'https:'
        },

        guiOptions: {
            consentModal: {
                layout: 'box',
                position: 'bottom right',
                equalWeightButtons: true,
                flipButtons: false
            },
            preferencesModal: {
                layout: 'box',
                equalWeightButtons: true,
                flipButtons: false
            }
        },

        categories: {
            necessary: {
                enabled: true,
                readOnly: true
            },
            analytics: {
                enabled: false,
                readOnly: false,
                autoClear: {
                    cookies: [
                        { name: /^_ga/ },
                        { name: '_gid' },
                        { name: /^_gat/ }
                    ]
                }
            },
            marketing: {
                enabled: false,
                readOnly: false,
                autoClear: {
                    cookies: [
                        { name: /^_fbp/ },
                        { name: /^_gcl/ },
                        { name: /^li_/ }
                    ]
                }
            }
        },

        language: {
            default: 'fr',
            autoDetect: 'document',

            translations: {
                fr: {
                    consentModal: {
                        label: 'Gestion des cookies',
                        title: 'Respect de votre vie privée',
                        description: 'Nous utilisons des cookies pour assurer le bon fonctionnement du site, mesurer notre audience et améliorer votre expérience de navigation. Vous pouvez tout accepter, tout refuser ou personnaliser vos préférences.',
                        acceptAllBtn: 'Tout accepter',
                        acceptNecessaryBtn: 'Tout refuser',
                        showPreferencesBtn: 'Personnaliser',
                        footer: `
                            <a href="/mentions-legales/">Mentions légales</a>
                        `
                    },
                    preferencesModal: {
                        title: 'Centre de préférences des cookies',
                        acceptAllBtn: 'Tout accepter',
                        acceptNecessaryBtn: 'Tout refuser',
                        savePreferencesBtn: 'Enregistrer mes choix',
                        closeIconLabel: 'Fermer',
                        serviceCounterLabel: 'Service(s)',
                        sections: [
                            {
                                title: 'Gestion de vos préférences',
                                description: 'Ce panneau vous permet d’activer ou de désactiver les différents traceurs déposés sur votre terminal lors de votre visite sur le site du cabinet Sountsou.'
                            },
                            {
                                title: 'Cookies strictement nécessaires',
                                description: 'Ces cookies sont indispensables au fonctionnement technique du site (sécurité, affichage responsive, enregistrement de vos choix de cookies). Ils ne peuvent pas être désactivés.',
                                linkedCategory: 'necessary'
                            },
                            {
                                title: 'Cookies de mesure d’audience',
                                description: 'Ces cookies nous permettent de mesurer la fréquentation et l’utilisation de notre site web de façon globale, afin d’en optimiser la performance et l’ergonomie.',
                                linkedCategory: 'analytics',
                                cookieTable: {
                                    caption: 'Liste des cookies de mesure d’audience',
                                    headers: {
                                        name: 'Cookie',
                                        service: 'Service',
                                        description: 'Finalité',
                                        duration: 'Durée de conservation'
                                    },
                                    body: [
                                        {
                                            name: '_ga, _ga_*',
                                            service: 'Google Analytics 4',
                                            description: 'Comptage des visites et analyse globale du trafic.',
                                            duration: '13 mois'
                                        },
                                        {
                                            name: '_gid',
                                            service: 'Google Analytics',
                                            description: 'Distinction statistique des visiteurs uniques.',
                                            duration: '24 heures'
                                        }
                                    ]
                                }
                            },
                            {
                                title: 'Cookies marketing et réseaux sociaux',
                                description: 'Ces cookies permettent d’interagir avec nos réseaux sociaux professionnels et de mesurer la visibilité de nos publications.',
                                linkedCategory: 'marketing',
                                cookieTable: {
                                    caption: 'Liste des cookies marketing',
                                    headers: {
                                        name: 'Cookie',
                                        service: 'Service',
                                        description: 'Finalité',
                                        duration: 'Durée de conservation'
                                    },
                                    body: [
                                        {
                                            name: 'li_*',
                                            service: 'LinkedIn',
                                            description: 'Mesure de l’engagement et suivi des interactions LinkedIn.',
                                            duration: '90 jours'
                                        }
                                    ]
                                }
                            },
                            {
                                title: 'Plus d’informations',
                                description: 'Pour toute information complémentaire sur notre politique de confidentialité et la gestion de vos données personnelles, veuillez consulter notre page <a href="/mentions-legales/">Mentions légales</a>.'
                            }
                        ]
                    }
                },
                en: {
                    consentModal: {
                        label: 'Cookie Consent',
                        title: 'Privacy and Cookies',
                        description: 'We use cookies to ensure optimal website operation, measure audience, and enhance your browsing experience. You can accept all, reject non-essential cookies, or manage your preferences at any time.',
                        acceptAllBtn: 'Accept all',
                        acceptNecessaryBtn: 'Reject all',
                        showPreferencesBtn: 'Preferences',
                        footer: `
                            <a href="/mentions-legales/">Legal notice</a>
                        `
                    },
                    preferencesModal: {
                        title: 'Cookie Preferences Center',
                        acceptAllBtn: 'Accept all',
                        acceptNecessaryBtn: 'Reject all',
                        savePreferencesBtn: 'Save preferences',
                        closeIconLabel: 'Close',
                        serviceCounterLabel: 'Service(s)',
                        sections: [
                            {
                                title: 'Manage your preferences',
                                description: 'Use this panel to customize which cookies and trackers are stored on your device when browsing Sountsou website.'
                            },
                            {
                                title: 'Strictly necessary cookies',
                                description: 'These cookies are required for fundamental site operations (security, accessibility, remembering your consent choices). They cannot be disabled.',
                                linkedCategory: 'necessary'
                            },
                            {
                                title: 'Analytics cookies',
                                description: 'These cookies allow us to collect aggregated statistics on site traffic to improve performance and user experience.',
                                linkedCategory: 'analytics',
                                cookieTable: {
                                    caption: 'Analytics cookies list',
                                    headers: {
                                        name: 'Cookie',
                                        service: 'Service',
                                        description: 'Purpose',
                                        duration: 'Duration'
                                    },
                                    body: [
                                        {
                                            name: '_ga, _ga_*',
                                            service: 'Google Analytics 4',
                                            description: 'Audience measurement and aggregated traffic analysis.',
                                            duration: '13 months'
                                        },
                                        {
                                            name: '_gid',
                                            service: 'Google Analytics',
                                            description: 'Unique session identifier.',
                                            duration: '24 hours'
                                        }
                                    ]
                                }
                            },
                            {
                                title: 'Marketing and social cookies',
                                description: 'These cookies enable integration with social platforms and track engagement with our communications.',
                                linkedCategory: 'marketing',
                                cookieTable: {
                                    caption: 'Marketing cookies list',
                                    headers: {
                                        name: 'Cookie',
                                        service: 'Service',
                                        description: 'Purpose',
                                        duration: 'Duration'
                                    },
                                    body: [
                                        {
                                            name: 'li_*',
                                            service: 'LinkedIn',
                                            description: 'Engagement measurement and tracking.',
                                            duration: '90 days'
                                        }
                                    ]
                                }
                            },
                            {
                                title: 'More information',
                                description: 'For inquiries regarding our privacy policy, please consult our <a href="/mentions-legales/">Legal Notice</a>.'
                            }
                        ]
                    }
                }
            }
        },

        onFirstConsent: ({ cookie }) => {
            updateConsentSignals(cookie);
        },

        onConsent: ({ cookie }) => {
            updateConsentSignals(cookie);
        },

        onChange: ({ cookie }) => {
            updateConsentSignals(cookie);
        }
    });
}
